import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import type { Schedule, ScheduleItem } from "src/types";

import type * as rootReducer from "@/redux/root-reducer";
import formatLocalDatetimeString from "@/utils/dates/formatLocalDatetimeString";
import endpoints from "@/utils/endpoints";
import { authenticatedFetch } from "@/utils/fetchJSON";
import { isError } from "@/utils/is-error";

import { getState } from "../helpers";
import { getIsFirstLoading } from "../requests/requests-reducer";

const get = endpoints();

export const fetchSchedules = createAsyncThunk<ScheduleItem[], string>(
  "schedules/fetch",
  async (slug: string, { rejectWithValue }) => {
    const response = await authenticatedFetch.fetchJSON<ScheduleItem[]>(
      get("SCHEDULES_API")(slug)
    );

    if (isError(response)) {
      toast.error(response.description, { toastId: response.description });
      return rejectWithValue(response);
    }

    return response;
  }
);

export const schedulesAdapter = createEntityAdapter<ScheduleItem | Schedule>({
  selectId: (schedule) =>
    (schedule as ScheduleItem).id || (schedule as Schedule).date,
});

const schedulesSlice = createSlice({
  name: "schedules",
  initialState: schedulesAdapter.getInitialState(),
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchSchedules.fulfilled, schedulesAdapter.upsertMany);
  },
});

const { selectAll: getAllSchedules } = schedulesAdapter.getSelectors(
  (state: rootReducer.RootState) => state.schedules
);

export const groupSchedules = (schedules: ScheduleItem[]): Schedule[] =>
  schedules.reduce((acc: Schedule[], schedule: ScheduleItem) => {
    const formattedDate = formatLocalDatetimeString(
      schedule.time_start,
      "MMMM dd"
    );

    const existed = acc.find((s) => s.date === formattedDate);
    if (existed) {
      existed.schedules.push(schedule);
      return acc;
    }

    acc.push({
      date: formattedDate,
      schedules: [schedule],
    });

    return acc;
  }, []);

export const groupSchedulesWithTimezone = (
  schedules: ScheduleItem[],
  timeZone?: string
): Schedule[] =>
  schedules.reduce((acc: Schedule[], schedule: ScheduleItem) => {
    const formattedDate = formatLocalDatetimeString(
      schedule.time_start,
      "MMMM dd",
      timeZone
    );

    const existed = acc.find((s) => s.date === formattedDate);
    if (existed) {
      existed.schedules.push(schedule);
      return acc;
    }

    acc.push({
      date: formattedDate,
      schedules: [schedule],
    });

    return acc;
  }, []);

const formatScheduleDates = (schedules: Schedule[]) =>
  schedules.map((s) => ({
    ...s,
    date: formatLocalDatetimeString(s.date, "MMMM dd"),
  }));

const getGroupedSchedules = createSelector(getAllSchedules, (schedules) => {
  const id = (schedules[0] as ScheduleItem)?.id;
  if (id) {
    return groupSchedules(schedules as ScheduleItem[]);
  } else {
    return formatScheduleDates(schedules as Schedule[]);
  }
});

const getIsSchedulesFirstLoading = createSelector(getState, (state) => {
  return getIsFirstLoading(state, fetchSchedules);
});

export { getAllSchedules, getGroupedSchedules, getIsSchedulesFirstLoading };

export default schedulesSlice.reducer;
